import {
  addWeeks,
  eachDayOfInterval,
  format as dateFnsFormat,
  lastDayOfWeek,
  startOfWeek,
} from 'date-fns';
import { lt as locale } from 'date-fns/locale';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock_gr.svg';
import Form from '../../../components/shared/Form/Form';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import InlineLoader from '@/components/shared/InlineLoader/InlineLoader';
import { Button } from '@/components/ui/button';
import ServiceQueueUserInformationDialog from '@/v2/pages/ServicesPage/Components/ServiceQueueUserInformationDialog';
import { useUserState } from '@/components/context/UserContext';
import { useHistory } from 'react-router-dom';

const VILNIUS_TIMEZONE = 'Europe/Vilnius';
const SLOTS_REFETCH_INTERVAL_MS = 30000;
const MAX_SLOTS_FETCHES_FOR_SAME_INTERVAL = 21;

const Calendar = ({
  filter,
  dataRequest,
  onChange,
  selected,
  error,
  refresh,
  psdfService,
  hasNearestPsdfDate,
  defaultData = new Date(),
  widget,
  extraPrice,
  servicePrice,
  refreshOfFilter,
  isHalfMrt,
  workplaceId,
  service,
  psdfPrice,
  hasSlots,
  visitPaymentTypeFilter,
  stopLoading,
  showQueue,
}) => {
  const { t } = useTranslation();
  const userState = useUserState();
  const history = useHistory();

  const slotsFetchCounterRef = useRef({
    key: null,
    count: 0,
    redirected: false,
  });

  const format = (date, formatStr = 'PP') =>
    dateFnsFormat(date, formatStr, { locale });

  const getLocalDateFromVilniusInput = (value) => {
    if (!value) {
      return new Date();
    }

    const dateKey = moment(value).tz(VILNIUS_TIMEZONE).format('YYYY-MM-DD');
    const [year, month, dayOfMonth] = dateKey.split('-').map(Number);

    return new Date(year, month - 1, dayOfMonth);
  };

  const getDateKey = (date) => dateFnsFormat(date, 'yyyy-MM-dd');

  const getSlotVilniusDateKey = (date) =>
    moment(date).tz(VILNIUS_TIMEZONE).format('YYYY-MM-DD');

  const timeSlotTime = (timeSlotDate) =>
    moment(timeSlotDate).tz(VILNIUS_TIMEZONE).format('HH:mm');

  const initialDay = filter
    ? getLocalDateFromVilniusInput(filter)
    : getLocalDateFromVilniusInput(defaultData);

  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(initialDay);
  const [currentDay, setCurrentDay] = useState();
  const [timeSlots, setTimeSlots] = useState([]);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [loadingSlotId, setLoadingSlotId] = useState(null);
  const [addToQueueDialogOpen, setAddToQueueDialogOpen] = useState(false);

  const weekOptions = { weekStartsOn: 1 };
  const weekStart = startOfWeek(day, weekOptions);
  const endOfWeek = lastDayOfWeek(day, weekOptions);
  const weekStartDate = getDateKey(weekStart);
  const endOfWeekDate = getDateKey(endOfWeek);
  const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek });

  useEffect(() => {
    if (!stopLoading) {
      setLoadingSlotId(null);
    }
  }, [stopLoading]);

  useEffect(() => {
    if (!hasSlots) {
      setLoading(false);
      return;
    }

    if (!filter) {
      return;
    }

    let isMounted = true;

    const serviceId = service?.serviceId;
    const serviceWorkplaceId = service?.workplaceId ?? workplaceId;

    const fetchCounterKey = [
      serviceId,
      serviceWorkplaceId,
      weekStartDate,
      endOfWeekDate,
    ].join('|');

    const requestSlots = (showLoader = false) => {
      if (slotsFetchCounterRef.current.key !== fetchCounterKey) {
        slotsFetchCounterRef.current = {
          key: fetchCounterKey,
          count: 0,
          redirected: false,
        };
      }

      if (
        slotsFetchCounterRef.current.count >=
        MAX_SLOTS_FETCHES_FOR_SAME_INTERVAL
      ) {
        if (!slotsFetchCounterRef.current.redirected) {
          slotsFetchCounterRef.current.redirected = true;
          history.push('/');
        }

        return;
      }

      slotsFetchCounterRef.current.count += 1;

      if (showLoader) {
        setLoading(true);
      }

      dataRequest(
        weekStartDate,
        endOfWeekDate,
        'all',
        serviceWorkplaceId,
        serviceId,
      )
        .then((data) => {
          if (!isMounted) {
            return;
          }

          if (data) {
            let filteredData = data;

            if (visitPaymentTypeFilter === 2) {
              filteredData = filteredData.filter((x) => !x.isPaid);
            }

            setTimeSlots(filteredData);
            setCurrentDay(day);
          }

          if (showLoader) {
            setLoading(false);
          }
        })
        .catch(() => {
          if (!isMounted) {
            return;
          }

          if (showLoader) {
            setLoading(false);
          }
        });
    };

    requestSlots(true);

    const intervalId = setInterval(() => {
      requestSlots(false);
    }, SLOTS_REFETCH_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [day]);

  const getTimeSlots = useCallback(() => {
    const slots = [];

    weekDays.forEach((weekDay) => {
      if (Array.isArray(timeSlots)) {
        const weekDayKey = getDateKey(weekDay);

        let daySlots = timeSlots.filter(
          (time) =>
            getSlotVilniusDateKey(time.startDate) === weekDayKey &&
            (time.statusName === 'Available' ||
              (time.duplicatedTimeSlotId !== null
                ? selected === time.duplicatedTimeSlotId
                : selected === time.timeSlotId)),
        );

        if (widget) {
          const filteredSlots = daySlots.filter(
            (ts) =>
              ts.statusName === 'Available' &&
              !ts.isReserved &&
              ts.visitStatus === null,
          );

          daySlots = filteredSlots.slice(0, 3);
        }

        slots.push(daySlots);
      }
    });

    const maxLength = slots.reduce(
      (r, s) => (r.length > s.length ? r : s),
      [],
    ).length;

    return slots.map((slot) => [
      ...slot,
      ...new Array(maxLength - slot.length).fill(0),
    ]);
  }, [selected, timeSlots, weekDays, widget]);

  function changeWeek(amount) {
    const newDay = addWeeks(day, amount);

    setLoading(true);
    setDay(newDay);
    setCurrentDay(newDay);
  }

  function showBackButton() {
    return new Date() < new Date(weekStartDate);
  }

  const isEmpty = !getTimeSlots().filter((arr) => !!arr.length).length;

  return (
    <>
      <div className="bg-[#ffffff] rounded-2xl p-0 md:p-5 select-none">
        <div className="flex items-center justify-center py-6">
          {showBackButton() && hasSlots && !widget && (
            <div
              className="h-7 w-7 rounded bg-[#F9FCFF] flex items-center justify-center cursor-pointer hover:opacity-60 mr-3"
              onClick={() => changeWeek(-1)}
            >
              <ChevronLeft className="text-[#1F4B81]" />
            </div>
          )}

          <div className="text-xl font-extrabold text-center min-w-0 sm:min-w-[340px]">
            <span className="sm:hidden">
              <span className="whitespace-nowrap">
                {format(weekStart, 'yyyy-MM-dd')}
              </span>
              {' – '}
              <span className="whitespace-nowrap">
                {format(endOfWeek, 'yyyy-MM-dd')}
              </span>
            </span>

            <span className="hidden sm:inline">
              <span className="whitespace-nowrap">
                {format(weekStart, 'yyyy MMMM d')}
              </span>
              {' – '}
              <span className="whitespace-nowrap">
                {`${format(endOfWeek, 'MMMM d')} d.`}
              </span>
            </span>
          </div>

          {!widget && hasSlots && (
            <div
              className="h-7 w-7 rounded bg-[#F9FCFF] flex items-center justify-center cursor-pointer hover:opacity-60 ml-3"
              onClick={() => changeWeek(1)}
            >
              <ChevronRight className="text-[#1F4B81]" />
            </div>
          )}
        </div>

        <div className="rounded-md bg-white">
          <div className="grid grid-cols-7 gap-2 pb-2 text-center h-13 ">
            {weekDays.map((day, i) => (
              <div
                key={i}
                className="bg-[#F9FCFF] text-sm md:text-md text-[#1F4B81] font-semibold flex items-center justify-center h-13 rounded"
              >
                {format(day, 'ccc. MM-dd').replace(/^./, (c) =>
                  c.toUpperCase(),
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {loading ? (
              <div className="col-span-7 h-[320px] flex justify-center items-center">
                <InlineLoader light={true} />
              </div>
            ) : isEmpty ? (
              <div className="col-span-7 h-[320px] flex flex-col justify-center items-center text-center text-gray-400 text-base max-w-md mx-auto px-4">
                {!showQueue && (
                  <div>
                    <ClockIcon className="mx-auto mb-3" />
                    <div>{t('validation.calendarNoAvailableTime')}</div>
                  </div>
                )}

                {showQueue && (
                  <p className="mt-6 max-w-md text-center text-base leading-6 font-semibold text-[#1F4B81]">
                    Neradote laisvų vizitų, tačiau norėtumėte registruotis?
                    <br />
                    Spauskite{' '}
                    <button
                      type="button"
                      onClick={() => setAddToQueueDialogOpen(true)}
                      className="font-bold text-inherit underline decoration-2 underline-offset-2 hover:opacity-80 transition cursor-pointer"
                    >
                      ČIA
                    </button>{' '}
                    ir mes su Jumis susisieksime dėl galimybės registruotis į
                    eilę.
                  </p>
                )}
              </div>
            ) : (
              getTimeSlots().map((column, i) => (
                <div key={i} className="flex flex-col">
                  {column
                    .slice(0, showAllSlots ? undefined : 5)
                    .map((item, ii) => (
                      <div key={ii} className="p-1 h-13">
                        {item ? (
                          <div
                            onClick={async () => {
                              const isDisabled =
                                item.isReserved ||
                                (item.visitStatus &&
                                  Object.keys(item.visitStatus).length > 0) ||
                                (item.isPaid &&
                                  (servicePrice ?? 0) +
                                    item.additionalServicesPrice ===
                                    0) ||
                                (item.isExtra &&
                                  (extraPrice ?? 0) +
                                    item.additionalServicesPrice ===
                                    0);

                              if (!isDisabled) {
                                const slotId =
                                  item.duplicatedTimeSlotId ?? item.timeSlotId;

                                setLoadingSlotId(slotId);

                                try {
                                  await onChange(item);
                                } finally {
                                }
                              }
                            }}
                            className={`flex items-center justify-center text-xs md:text-sm rounded cursor-pointer h-full px-1
                              ${
                                item.isReserved ||
                                item.visitStatus !== null ||
                                (item.isPaid &&
                                  !(
                                    (servicePrice ?? 0) +
                                    item.additionalServicesPrice
                                  )) ||
                                (item.isExtra &&
                                  !(
                                    (extraPrice ?? 0) +
                                    item.additionalServicesPrice
                                  ))
                                  ? 'bg-gray-100 text-gray-400 cursor-default'
                                  : item.isExtra
                                    ? 'bg-[#F2F0FF] text-[#525252] border border-[#7F67DE]'
                                    : item.isPaid
                                      ? 'bg-[#EEF4FF] text-[#525252] border border-[#5180CA]'
                                      : 'bg-[#F8FFF8] text-[#525252] border border-[#72BE77]'
                              }
                              ${
                                selected ===
                                (item.duplicatedTimeSlotId ?? item.timeSlotId)
                                  ? 'font-bold border-2 border-black'
                                  : 'border-1'
                              }
                            `}
                          >
                            {loadingSlotId ===
                            (item.duplicatedTimeSlotId ?? item.timeSlotId) ? (
                              <InlineLoader light={true} />
                            ) : (
                              timeSlotTime(item.startDate)
                            )}
                          </div>
                        ) : (
                          <div className="bg-[#F8F8F8] rounded h-full" />
                        )}
                      </div>
                    ))}
                </div>
              ))
            )}
          </div>

          {!loading && !isEmpty && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => setShowAllSlots(!showAllSlots)}
                className="text-white font-medium text-sm hover:underline flex items-center"
              >
                <span className="mr-1">
                  {showAllSlots
                    ? 'Rodyti mažiau laikų'
                    : 'Rodyti daugiau laikų'}
                </span>
                {showAllSlots ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}

          <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-[#1F4B81]">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#EEF4FF] border border-[#5180CA]" />
              <span>Mokamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#F8FFF8] border border-[#72BE77]" />
              <span>PSDF kompensuojamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#F2F0FF] border border-[#7F67DE]" />
              <span>Papildomas mokamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
              <span>Užimtas laikas</span>
            </div>
          </div>
        </div>

        {error && <Form.Error>{error}</Form.Error>}
      </div>

      <ServiceQueueUserInformationDialog
        open={addToQueueDialogOpen}
        onOpenChange={setAddToQueueDialogOpen}
        isLoggedIn={!!userState?.email}
        initialPhoneNumber={userState?.phoneNumber || ''}
        initialEmail={userState?.email || ''}
        initialName={
          userState?.firstName
            ? `${userState?.firstName} ${userState?.lastName}`
            : ''
        }
        serviceId={service?.serviceId}
        workplaceId={service?.workplaceId}
        onAccept={(data) => {
          // logged in -> data === true
          // not logged in -> data = { phoneNumber, email }
        }}
      />
    </>
  );
};

Calendar.propTypes = {
  dataRequest: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default Calendar;
